import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { SystemIndexRanges } from "@graylog/server-api";
import HideOnCloud from "util/conditional/HideOnCloud";
import { defaultOnError } from "util/conditional/onError";
import NumberUtils from "util/NumberUtils";
import { Col, Row, Button } from "components/bootstrap";
import { Spinner } from "components/common";
import IndexRangeSummary from "components/indices/IndexRangeSummary";
import ShardMeter from "components/indices/ShardMeter";
import ShardRoutingOverview from "components/indices/ShardRoutingOverview";
import { INDICES_QUERY_KEY, deleteIndex } from "hooks/useIndices";
const IndexDetails = ({ index, indexName, indexRange = void 0, indexSetId = void 0, isDeflector }) => {
  const { t } = useTranslation("indexsets");
  const queryClient = useQueryClient();
  const _invalidate = useCallback(() => queryClient.invalidateQueries({ queryKey: INDICES_QUERY_KEY }), [queryClient]);
  const _onRecalculateIndex = useCallback(() => {
    if (window.confirm(t("indices.details.confirm_recalculate", { indexName }))) {
      defaultOnError(
        SystemIndexRanges.rebuildIndex(indexName),
        t("indices.details.recalculate_failed", { indexName }),
        t("indices.details.recalculate_failed_title", { indexName })
      ).then(() => {
        if (indexSetId) {
          _invalidate();
        }
      });
    }
  }, [indexName, indexSetId, _invalidate, t]);
  const _onDeleteIndex = useCallback(() => {
    if (window.confirm(t("indices.details.confirm_delete", { indexName }))) {
      deleteIndex(indexName).then(() => {
        if (indexSetId) {
          _invalidate();
        }
      });
    }
  }, [indexName, indexSetId, _invalidate, t]);
  const actionButtons = useMemo(() => {
    if (isDeflector) {
      return /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(Button, { bsStyle: "danger", bsSize: "xs", disabled: true }, t("indices.details.active_write_index")));
    }
    return /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(Button, { bsStyle: "warning", bsSize: "xs", onClick: _onRecalculateIndex }, t("indices.details.recalculate")), " ", /* @__PURE__ */ React.createElement(Button, { bsStyle: "danger", bsSize: "xs", onClick: _onDeleteIndex }, t("indices.details.delete")));
  }, [isDeflector, _onDeleteIndex, _onRecalculateIndex, t]);
  if (!index || !index.all_shards) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  return /* @__PURE__ */ React.createElement("div", { className: "index-info" }, /* @__PURE__ */ React.createElement(IndexRangeSummary, { indexRange }), " ", /* @__PURE__ */ React.createElement(HideOnCloud, null, t("indices.details.stats", {
    segments: NumberUtils.formatNumber(index.all_shards.segments),
    contexts: NumberUtils.formatNumber(index.all_shards.open_search_contexts),
    deleted: NumberUtils.formatNumber(index.all_shards.documents.deleted)
  }), /* @__PURE__ */ React.createElement(Row, { style: { marginBottom: "10" } }, /* @__PURE__ */ React.createElement(Col, { md: 4, className: "shard-meters" }, /* @__PURE__ */ React.createElement(ShardMeter, { title: t("indices.details.primary_shard_operations"), shardMeter: index.primary_shards })), /* @__PURE__ */ React.createElement(Col, { md: 4, className: "shard-meters" }, /* @__PURE__ */ React.createElement(ShardMeter, { title: t("indices.details.total_shard_operations"), shardMeter: index.all_shards }))), /* @__PURE__ */ React.createElement(ShardRoutingOverview, { routing: index.routing, indexName })), /* @__PURE__ */ React.createElement("hr", { style: { marginBottom: "5", marginTop: "10" } }), actionButtons);
};
export default IndexDetails;
